/**
 * 레시피 목 데이터.
 * 출처: 와이어프레임 `SAMPLE_RECIPE_JSON`(2031행) · `RCP_REAG0`(2040행) · 레시피 목록 `R`(2081행).
 *
 * 🔴 **데모 레시피 이름을 바꿨다: `IHC-CD3 v3` → `ICC-CK v3`.**
 *    까닭 — 이 장비는 ICC(면역세포화학) 장비인데 `IHC`(면역조직화학) 접두어가 붙어 있었고,
 *    표준 레시피 이름이 이미 「CTCells ICC 염색 v1」이라 한 화면에 두 접두어가 섞였다.
 *    마커도 `CD3`(T세포 표면)에서 `CK`(cytokeratin — 상피 유래 세포 식별 마커)로 바꿨다.
 *    ⚠ **저쪽 세션이 지은 임의 이름을 내가 다시 임의로 고친 것이다.** eric 이 실제 마커를 지정하면
 *      이 파일의 상수 하나만 바꾸면 된다. 근거는 docs/fixtures.md.
 */
import type {
  Recipe,
  RecipeListRow,
  ReagentDef,
  RecipeLabwareDef,
  StainingStepRef,
} from '@ctcells/contract'

/**
 * 시약 채널 8개. 상온 랙 5 + 냉장 블록 3(4℃). **랩웨어 자리가 곧 보관 온도다.**
 *
 * 🔴 **종전 이름은 `REAGENT_WELLS` 였고 값이 `RA1…RC3` 였다**(2026-09-21 교체). 그 값은
 *    실물 레시피 어디에도 없다 — 서버가 주는 `reagents[*]` 에 `well` 키 자체가 «없고»
 *    (14건 · 112칸 전수), 자리는 `labware_id`(`staining.reagent_bay.ambient-1`)와
 *    `volumeByWell` 의 «키»(`A1`…)가 말한다. 되살리지 마라.
 */
export const REAGENT_CHANNEL_MAX = 8

/*
 * 🔴 **서버 DTO 모양 그대로다**(2026-09-21 · 실물 `GET /recipes` 원문으로 다시 맞췄다).
 *
 *    | 종전 목        | 실물                                                             |
 *    | -------------- | ---------------------------------------------------------------- |
 *    | `well: 'RA1'`  | 그런 키가 «없다». 자리는 `labware_id` + `volumeByWell` 의 키다    |
 *    | `lot` 키 부재  | **`lot: ""`(빈 문자열)로 «있다»** — 이 차이가 실제 버그를 숨겼다 |
 *    | 시약 랙 5+3개  | 랩웨어는 **3개**(`ambient-1` · `ambient-2` · `chilled`)이고        |
 *    |                | 여러 채널이 **같은 랙의 다른 웰**을 쓴다                          |
 *    | `liquid_class` | 실물 8칸 전부 `aqueous_default` 하나다(`surfactant` 는 없다)      |
 *
 * 🔴 **`lot` 을 키째 빼지 마라.** 종전 목이 그랬고, 그래서 「작업마다 lot 을 받는 채널」 판정이
 *    `def.lot === undefined` 로 짐작하는 채로 **목에서만 맞아 보였다**. 실물에서는 8칸 전부
 *    `""` 라 그 판정이 언제나 거짓이었다. 지금은 `runtime_parameters` 가 말한다
 *    (`recipeForm.lotBoundChannels`).
 * ⚠ 용량 단위는 µL 다. 실물 값 크기를 그대로 살렸다(워싱용액 5만 µL 급 · 냉장 항체 1,500 µL 급).
 *
 * 🔴 **시약대 인스턴스 접미는 «하이픈»이다(`ambient-1` · `ambient-2`) — 2026-10-11 실물로 바꿨다.**
 *    2026-09-22 에는 실서버 커밋 레시피가 아직 «점»(`ambient.1`)이라 「실물이 새 꼴로 갈리는 날 함께 바꾼다」고 남겨 뒀는데,
 *    그날이 왔다 — dev2 v0.9.4 실측(2026-10-11 · 배치 8건 `GET /batches` 의 `reagent_basis[].labwareId` 전부 ·
 *    염색 모듈 `consumables.reagents_by_well` 전부)이 `staining.reagent_bay.ambient-1` · `-2` · `chilled` 다.
 *    등록부(`positions.yaml`)도 접미 없는 `staining.reagent_bay.ambient` 를 「옛 레시피 전용」으로 남겼다.
 *    ⚠ 팁 자리(`staining.tip_bay.1`)는 이번에 실물을 재지 않아 그대로 둔다 — 재고 나서 같이 바꾼다.
 *    ⚠ 화면은 두 꼴을 다 받는다(`reagentBayName` 의 `[.-]` · 데크 랙 대조) — 옛 커밋 레시피가 남아 있을 수 있어서다.
 */
const REAGENTS: Record<string, ReagentDef> = {
  '1': {
    channel: 1,
    name: 'DPBS',
    display_name: '워싱용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.ambient-1',
    volumeByWell: { A1: 50000, A2: 50000, A3: 26000 },
    use_order: ['A1', 'A2', 'A3'],
    lot: '',
  },
  '2': {
    channel: 2,
    name: '16% 포름알데히드',
    display_name: '고정용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.ambient-2',
    volumeByWell: { A1: 7200 },
    lot: '',
  },
  '3': {
    channel: 3,
    name: '0.2% Triton X-100',
    display_name: '펌용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.ambient-2',
    volumeByWell: { B1: 7200 },
    lot: '',
  },
  '4': {
    channel: 4,
    name: 'PBS-T',
    display_name: '항체 워싱 용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.ambient-1',
    volumeByWell: { B1: 45000 },
    lot: '',
  },
  /* ⚠ 냉장 세 채널만 `storage_celsius` 를 가진다 — 상온 채널에는 그 키가 «없다»(실물 그대로) */
  '5': {
    channel: 5,
    name: '>1% 1차 항체, PBS-T',
    display_name: '1차 항체 용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.chilled',
    volumeByWell: { A1: 1500, B1: 1200 },
    use_order: ['A1', 'B1'],
    storage_celsius: 4,
    lot: '',
  },
  '6': {
    channel: 6,
    name: '>1% 2차 항체, PBS-T',
    display_name: '2차 항체 용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.chilled',
    volumeByWell: { A2: 1500, B2: 1200 },
    use_order: ['A2', 'B2'],
    storage_celsius: 4,
    lot: '',
  },
  '7': {
    channel: 7,
    name: '>1% Hoechst 33342, DPBS',
    display_name: 'DAPI 용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.chilled',
    volumeByWell: { A3: 1500, B3: 1500, C3: 1500 },
    use_order: ['A3', 'B3', 'C3'],
    storage_celsius: 4,
    lot: '',
  },
  '8': {
    channel: 8,
    name: 'PBS-T',
    display_name: '블로킹용액',
    liquid_class: 'aqueous_default',
    labware_id: 'staining.reagent_bay.ambient-2',
    volumeByWell: { C1: 9000 },
    lot: '',
  },
}

/**
 * 96 팁 랙의 웰 이름과 `ordering` — **열(column) 우선**이다(Opentrons 정의 스키마 그대로).
 * 🔴 바깥이 «열»이라 `ordering.length === 12` · `ordering[0].length === 8` 이다. 뒤집지 마라 —
 *    뒤집으면 격자가 「12 × 8」로 읽힌다(`labwareModel.readOrderingGrid` 주석 참조).
 */
const TIP_RACK_ROWS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const
const TIP_RACK_ORDERING: string[][] = Array.from({ length: 12 }, (_unused, column) =>
  TIP_RACK_ROWS.map((row) => `${row}${String(column + 1)}`),
)
const TIP_RACK_WELLS: string[] = TIP_RACK_ORDERING.flat()

/**
 * 레시피 안에 박히는 랩웨어 — 🔴 **실물은 14개이고 전부 `full` 이다**(2026-09-21 실측).
 *
 * 종전 목은 5개 전부 `simple` 이었고 격자·웰 이름이 «없어서» 상세의 「랩웨어」 탭이 목에서만
 * 텅 비어 보였다. 아래는 실물 14개 중 **모양이 서로 다른 것들을 골라** 옮긴 것이다:
 *  · `hypicc.tray.1` — `full` 인데 `ordering` 이 «없다»(웰 이름만 있다) ⇒ 격자는 `—`
 *  · `tips_1000_a` — `ordering` 12열 × 8행 ⇒ 격자 `8 × 12`
 *  · `staining.reagent_bay.ambient-1` — **`display_name` 이 «없다»** ⇒ 이름 칸이 `—`
 *  · `centrifuge.waste.1` — 웰 1칸짜리 폐기통(염색 절 밖의 랩웨어도 레시피에 담긴다)
 *
 * ⚠ 실물은 `brand`·`wells`·`dimensions`·`tip_length_mm` 같은 키를 더 가진다(Opentrons 정의
 *   스키마 그대로다). 화면이 읽지 않는 칸이라 여기 옮기지 않았다 — **없는 것이 아니라 안 옮긴 것**이다.
 */
const RECIPE_LABWARE: RecipeLabwareDef[] = [
  {
    id: 'hypicc.tray.1',
    type: 'hypicc_chamber',
    layout_detail: 'full',
    location: 'staining.tray',
    display_name: '하이픽 트레이 3×3 (시료 8 + Reference 1)',
    well_names: ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'],
    default_well_volume_ul: 1200,
  },
  {
    id: 'tips_1000_a',
    type: 'tip_rack',
    layout_detail: 'full',
    location: 'staining.tip_bay.1',
    display_name: '1000µL 팁 랙 A (96)',
    well_names: TIP_RACK_WELLS,
    ordering: TIP_RACK_ORDERING,
  },
  {
    id: 'staining.reagent_bay.ambient-1',
    type: 'reagent_reservoir',
    layout_detail: 'full',
    location: 'staining.reagent_bay.ambient-1',
    well_names: ['A1', 'B1', 'A2', 'B2', 'A3', 'B3'],
    ordering: [
      ['A1', 'B1'],
      ['A2', 'B2'],
      ['A3', 'B3'],
    ],
  },
  {
    id: 'staining.reagent_bay.chilled',
    type: 'reagent_reservoir',
    layout_detail: 'full',
    location: 'staining.reagent_bay.chilled',
    well_names: ['A1', 'B1', 'C1', 'D1', 'A2', 'B2', 'C2', 'D2', 'A3', 'B3', 'C3', 'D3'],
    ordering: [
      ['A1', 'B1', 'C1', 'D1'],
      ['A2', 'B2', 'C2', 'D2'],
      ['A3', 'B3', 'C3', 'D3'],
    ],
  },
  {
    id: 'centrifuge.waste.1',
    type: 'reagent_reservoir',
    layout_detail: 'full',
    location: 'centrifuge.waste',
    display_name: '원심분리기 폐기통(TC·챔버)',
    well_names: ['A1'],
  },
]

/**
 * 작업 생성 때 받을 값 — 🔴 **실물이 주는데 목에 없어서 화면이 못 보고 있었다**(2026-09-21 추가).
 * 실물 표준 레시피의 세 줄 그대로다. `pAb_lot`·`sAb_lot` 의 `binds_to` 가
 * 「5·6번 채널의 lot 을 작업 생성 때 받는다」를 말한다(`recipeForm.lotBoundChannels`).
 */
const RUNTIME_PARAMETERS = [
  { name: 'sample_ids', type: 'csv', binds_to: 'batch.samples', mandatory: true },
  { name: 'pAb_lot', type: 'scalar', binds_to: 'reagents.5.lot', mandatory: true },
  { name: 'sAb_lot', type: 'scalar', binds_to: 'reagents.6.lot', mandatory: true },
]

/** ⚠ 실물은 이 한 가지뿐이다 — 다른 클래스를 지어내지 않는다 */
const LIQUID_CLASSES = { aqueous_default: { base: 'aqueous' } }

/**
 * 스텝 수: 원심 57 · 어셈블 101 · 염색 71 (README §16-9).
 * ⚠ 어셈블 수치는 메모리 노트 안에서 12 → 101 로 바뀐 이력이 있다. **서버 payload 로 확인해야 한다.**
 */
export const STEP_COUNTS = { centrifuge: 57, assembler: 101, staining: 71 } as const

const STAINING_OPERATIONS = ['aspirate', 'dispense', 'shake'] as const

/** 실물 전건이 같은 9웰을 쓴다(레시피 16건 · 스텝 1,136개 전수 · 2026-09-22) */
const STAINING_WELLS = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3']

/**
 * 염색 스텝 N개.
 *
 * 🔴 **동작마다 «키 집합»이 다르다 — 서버 DTO 모양 그대로다**(2026-09-22 전수 실측).
 *    종전 목은 `step_id`·`operation` 둘뿐이라 실물보다 «순했고», 그래서 표에 어떤 열이
 *    비는지·어떤 키가 빠지는지를 목에서 볼 수 없었다(CLAUDE.md 「fixture 는 서버 DTO
 *    모양을 그대로 따른다」).
 *
 *    | 동작     | 실물이 «주는» 키                                           |
 *    | -------- | ---------------------------------------------------------- |
 *    | dispense | `tip_policy` · `volume_ul` · `reagent_channel` (+`liquid_class` 일부) |
 *    | aspirate | `tip_policy` · `to_waste` · `waste_stream`                  |
 *    | shake    | `shake{rpm,duration_sec}`                                  |
 *
 * 🔴 **값이 없는 스텝은 키를 «뺀다».** `null`·`0` 으로 채우지 않는다 — 서버가 그렇게 준다.
 * ⚠ `wait_sec` 은 **한 스텝에도 없다**(실물 1,136개 전수 0건). 목에도 넣지 마라.
 */
function stainingSteps(count: number): StainingStepRef[] {
  return Array.from({ length: count }, (_unused, i): StainingStepRef => {
    const operation = STAINING_OPERATIONS[i % STAINING_OPERATIONS.length] ?? 'dispense'
    const base = { step_id: `s${i + 1}`, operation, target_wells: STAINING_WELLS }
    if (operation === 'aspirate') {
      return { ...base, tip_policy: 'per_sample', to_waste: true, waste_stream: 'biohazard' }
    }
    if (operation === 'shake') {
      return { ...base, shake: { rpm: 350, duration_sec: 300 } }
    }
    return {
      ...base,
      tip_policy: 'once',
      volume_ul: 1000,
      reagent_channel: 1,
      /* 실물은 일부 분주 스텝에만 붙는다(8/71) — 「전부 있는 값」으로 만들지 않는다 */
      ...(i % 9 === 1 ? { liquid_class: 'aqueous_default' } : {}),
    }
  })
}

const range = (n: number, make: (i: number) => { step_id: string }): { step_id: string }[] =>
  Array.from({ length: n }, (_unused, i) => make(i))

/**
 * 등록 시각. 🔴 **원값을 한 곳에 둔다** — 목록 줄(`RECIPE_LIST.created_at`)이 이것으로
 *    정렬하고 상세(`Recipe.created_at`)가 같은 값을 보인다. 두 벌로 적으면 어긋난다.
 * ⚠ `Recipe.created_at` 은 계약상 «선택»이라 그 칸을 통해 되읽으면 `string | undefined` 가
 *   된다. 목록 줄의 `created_at` 은 선택 칸이라 `undefined` 를 실을 수 없다(엄격 설정) —
 *   그래서 객체를 거치지 않고 이 상수를 직접 쓴다.
 */
const CREATED_STANDARD = '2026-09-08T10:00:00+09:00'
const CREATED_CK = '2026-09-11T14:31:00+09:00'
const CREATED_V0 = '2026-08-30T09:00:00+09:00'

/** 표준 ICC 염색 레시피(확정). 실행 중인 데모 작업이 이것을 쓴다 */
export const RECIPE_ICC_STANDARD: Recipe = {
  recipe_id: 'rcp-icc-std',
  name: 'CTCells ICC 염색 v1',
  version: 1,
  status: 'committed',
  schema_version: 'rcp/1.0',
  created_by: 'eric.cho',
  created_at: CREATED_STANDARD,
  payload: {
    schema_version: 'rcp/1.0',
    reagents: REAGENTS,
    /* 🔴 실물 전건이 주는 둘이다 — 목에서 빼면 「파라미터」·「시약」 탭이 목에서만 빈다 */
    runtime_parameters: RUNTIME_PARAMETERS,
    liquid_classes: LIQUID_CLASSES,
    labware: RECIPE_LABWARE,
    tray_path: {
      order: ['A1', 'A2', 'B1', 'B2', 'A3', 'B3', 'C1', 'C2', 'C3'],
      sample_groups: [
        { sample_ref: 'S1', role: 'patient', wells: ['A1', 'A2'] },
        { sample_ref: 'S2', role: 'patient', wells: ['B1', 'B2'] },
        { sample_ref: 'S3', role: 'patient', wells: ['A3', 'B3'] },
        { sample_ref: 'S4', role: 'patient', wells: ['C1', 'C2'] },
        { sample_ref: 'REF', role: 'reference', wells: ['C3'] },
      ],
    },
    centrifuge: {
      speed_rpm: 3000,
      duration_sec: 180,
      steps: range(STEP_COUNTS.centrifuge, (i) => ({ step_id: `c${i + 1}` })),
    },
    assembler: {
      uv_duration_sec: 20,
      uv_tilt_angle_deg: 0,
      load_tilt_angle_deg: 15,
      drain_angle_deg: 180,
      steps: range(STEP_COUNTS.assembler, (i) => ({ step_id: `a${i + 1}` })),
    },
    staining: {
      reagent_storage_celsius: 4,
      steps: stainingSteps(STEP_COUNTS.staining),
    },
    constraints: { max_allowed_delay_sec: 1800 },
  },
}

/** 개발 중 레시피(작성 중). 이름 근거는 파일 머리 주석 */
export const RECIPE_ICC_CK: Recipe = {
  ...RECIPE_ICC_STANDARD,
  recipe_id: 'rcp-icc-ck',
  name: 'ICC-CK v3',
  version: 3,
  status: 'draft',
  created_at: CREATED_CK,
}

/** 초기 검증용(보관). 염색 스텝이 15 로 짧다 */
export const RECIPE_ICC_V0: Recipe = {
  ...RECIPE_ICC_STANDARD,
  recipe_id: 'rcp-icc-v0',
  name: 'ICC v0',
  version: 1,
  status: 'archived',
  created_at: CREATED_V0,
  payload: {
    ...RECIPE_ICC_STANDARD.payload,
    staining: {
      reagent_storage_celsius: 4,
      steps: stainingSteps(15),
    },
  },
}

export const RECIPES: Recipe[] = [RECIPE_ICC_CK, RECIPE_ICC_STANDARD, RECIPE_ICC_V0]

/**
 * 레시피 목록 표(화면 전용 합성).
 * `description` · `usage_count` · 수정 표기는 계약 응답에 없다 — `RecipeListRow` 주석 참조.
 * 🔴 **`description` 줄을 되살리지 마라**(2026-09-21 제거). 계약에 없는 값이라 화면에서 열째
 *    걷었다 — 여기에 남아 있으면 다음 사람이 「응답에 있는 값」으로 착각한다.
 */
export const RECIPE_LIST: RecipeListRow[] = [
  {
    recipe_id: RECIPE_ICC_CK.recipe_id,
    name: RECIPE_ICC_CK.name,
    status: 'draft',
    version: 3,
    step_counts: { ...STEP_COUNTS },
    created_text: '오늘 14:31',
    created_by: 'eric.cho',
    /* 🔴 정렬은 표기가 아니라 «원값»으로 한다 — 원본 `Recipe` 의 것을 그대로 참조한다 */
    created_at: CREATED_CK,
  },
  {
    recipe_id: RECIPE_ICC_STANDARD.recipe_id,
    name: RECIPE_ICC_STANDARD.name,
    status: 'committed',
    version: 1,
    step_counts: { ...STEP_COUNTS },
    created_text: '09-08',
    created_by: 'eric.cho',
    created_at: CREATED_STANDARD,
  },
  {
    recipe_id: RECIPE_ICC_V0.recipe_id,
    name: RECIPE_ICC_V0.name,
    status: 'archived',
    version: 1,
    step_counts: { centrifuge: 57, assembler: 101, staining: 15 },
    created_text: '08-30',
    created_by: 'eric.cho',
    created_at: CREATED_V0,
  },
]

/*
 * 🔴 **`RECIPE_CHECKS` 를 되살리지 마라**(2026-09-20 제거). 「검증 3회 · 통과 2회 ·
 *    최근 14:31 · 예상 8시간 52분」과 레시피 이력 4줄을 담고 있었는데 **전부 지어낸 수**다 —
 *    어느 응답에도 그 집계가 없다(`contract/screen.ts` `RecipeCheckState` 자리의 주석).
 *    상세 화면은 검증·드라이런 자체를 그리지 않는다(2026-09-29 eric 「검증, 드라이런 이거 빼」).
 */
